import type { PlanSummary } from '@rheora/enrollment-contract';

import { headlineRateAt } from './planPricing';
import type { PriceDisclosure } from './price-disclosure.service';

/**
 * HOW THE SHELF IS ORDERED — and which number it orders by.
 *
 * ── WHAT WENT WRONG ────────────────────────────────────────────────────────
 * QA 2026-08-05 recorded the ONCR shelf at 1000 kWh rendering, in order: Tesla
 * Electric 21.8¢ (1-month), TXU Flex Rewards 22.0¢ (1-month), then 4Change 15.2¢,
 * Gexa 16.6¢, Frontier 16.7¢ … The effective order was `termMonths ASC, price ASC`,
 * so **the two most expensive plans on the shelf were shown first** and the cheapest
 * was third — on a page whose entire premise is comparing like for like.
 *
 * ⚠️ NOTHING IN US-Shop WAS SORTING. `/bff/plans` returns the catalogue in its own
 * order and `MeterPlansPage` rendered it as received, so "term first" was inherited
 * rather than chosen — nobody had decided it, which is exactly why it needed
 * deciding. Price-ascending is now the explicit default: on a §25.475 average-price
 * comparison shelf, cheapest-first is the honest ordering.
 *
 * ⚠️ PENDING COMPLIANCE CONFIRMATION. Whether a non-price default is permissible
 * here (and whether price-first disadvantages any tariff structure) is an open
 * question with `audit-compliance-reviewer`. The default is deliberately a ONE-LINE
 * change (`DEFAULT_PLAN_SORT`) and the shopper can re-sort, so whichever way that
 * lands is a small move rather than a rewrite.
 *
 * ── WHICH PRICE ────────────────────────────────────────────────────────────
 * The §25.475 comparison happens at standardised usage points, and each card
 * headlines the average price AT THE SHOPPER'S SELECTED USAGE — the disclosed EFL
 * figure verbatim at 500/1000/2000 kWh, the interpolated estimate in between
 * (`headlineRateAt`). That RENDERED figure is what this sorts on, not the raw
 * `rateCentsPerKwh` energy charge, which excludes TDU delivery and the monthly base
 * charge and is therefore a different number from the one on the card. Sorting by a
 * figure the customer cannot see would make the ordering unverifiable by eye — the
 * precise defect this replaces.
 *
 * Sorting the DISPLAYED value (rather than the underlying float) also guarantees
 * the visible sequence is monotonic: two plans shown as 18.2¢ never appear in an
 * order the customer would read as wrong because of hidden precision.
 *
 * ── PLANS WITH NO COMPARABLE FIGURE ────────────────────────────────────────
 * Two different situations, ONE ordering consequence. Price points are fetched
 * per plan, so some may be pending or failed while the shelf renders; and some
 * plans have no §25.475 average price AT ALL, because Pricing declines to publish
 * one for a plan whose advertised terms its calculation does not model.
 *
 * Both sort LAST, in catalogue order. Not because they are equivalent — they are
 * not, and their cards now say quite different things ("Calculating…", "Price
 * unavailable", and the withheld explanation) — but because ORDERING is a claim
 * about relative price, and neither has one to make. Guessing a position for a
 * price we do not have would be inventing a comparison; guessing one for a price
 * we have DECIDED not to publish would be worse, since the sort itself would then
 * assert the ranking we just refused to state.
 *
 * Pure: no React, no I/O, no float money math beyond the display estimate that
 * `planPricing` already owns.
 */

export const PLAN_SORT_IDS = ['price-asc', 'price-desc', 'term-asc'] as const;
export type PlanSortId = (typeof PLAN_SORT_IDS)[number];

/**
 * ⚠️ THE PRODUCT DECISION, IN ONE PLACE. Price ascending — the honest default for a
 * §25.475 comparison shelf. Pending compliance confirmation (see the file banner).
 */
export const DEFAULT_PLAN_SORT: PlanSortId = 'price-asc';

/** Narrow an arbitrary string (a URL param, a `<select>` value) to a sort id. */
export function isPlanSortId(value: string): value is PlanSortId {
  return PLAN_SORT_IDS.some((id) => id === value);
}

export interface PlanOrderingArgs {
  readonly sort: PlanSortId;
  /** The shopper's chosen monthly usage — the point the comparison is made at. */
  readonly monthlyKwh: number;
  /**
   * Price disclosures by `tariffId`. A missing entry means "not known yet"; a
   * `WITHHELD` entry means "there is no average price to compare".
   */
  readonly pricePoints: ReadonlyMap<string, PriceDisclosure>;
}

/**
 * The ¢/kWh figure THE CARD SHOWS for this plan, or `undefined` when there is no
 * such figure — because it has not arrived, arrived malformed, or is one we
 * deliberately do not publish.
 *
 * ⚠️ `WITHHELD` MUST RETURN `undefined`, NOT A DERIVED NUMBER. It would be easy
 * to reach for the energy charge here to keep the sort tidy; that would rank the
 * shelf by a figure the customer cannot see, on the exact plans where we have
 * ruled that no defensible figure exists.
 */
export function shelfRateFor(
  plan: PlanSummary,
  args: Pick<PlanOrderingArgs, 'monthlyKwh' | 'pricePoints'>,
): number | undefined {
  const disclosure = args.pricePoints.get(plan.tariffId);
  if (disclosure === undefined || disclosure.status === 'WITHHELD') return undefined;
  const shown = Number.parseFloat(headlineRateAt(disclosure.pricePoints, args.monthlyKwh));
  return Number.isFinite(shown) ? shown : undefined;
}

/** Ascending by the shown price; ties (and unknowns) fall through to the caller. */
function byShownPrice(
  a: number | undefined,
  b: number | undefined,
  direction: 1 | -1,
): number {
  // Unknown prices always sink, in BOTH directions — "we don't know" is never the
  // best offer and never the worst one either.
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  return (a - b) * direction;
}

/**
 * The shelf, ordered. Returns a new array; the input is never mutated (the query
 * cache owns it).
 *
 * Every comparator ends in a plan-name tiebreak so the order is deterministic —
 * two plans at the same price must not swap places between renders.
 */
export function sortPlans(plans: readonly PlanSummary[], args: PlanOrderingArgs): PlanSummary[] {
  const rate = (plan: PlanSummary): number | undefined => shelfRateFor(plan, args);
  const byName = (a: PlanSummary, b: PlanSummary): number =>
    a.planName.localeCompare(b.planName);

  const comparators: Record<PlanSortId, (a: PlanSummary, b: PlanSummary) => number> = {
    'price-asc': (a, b) => byShownPrice(rate(a), rate(b), 1) || byName(a, b),
    'price-desc': (a, b) => byShownPrice(rate(a), rate(b), -1) || byName(a, b),
    // The previous (inherited, never-chosen) order, kept as an explicit CHOICE so a
    // shopper who wants short contracts first can still get them.
    'term-asc': (a, b) =>
      a.termMonths - b.termMonths || byShownPrice(rate(a), rate(b), 1) || byName(a, b),
  };

  return [...plans].sort(comparators[args.sort]);
}
